import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Tooltip from '@material-ui/core/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Container from '../../GeneralComponents/Container';
import { Topbar, Sidebar, Table } from '../components';
import setGlobalLoader from '../../../redux/actions/loaderActions';
import { getBatches, deleteBatch } from '../../../redux/actions/apiActions';

const Batches = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const tableRef = useRef(null);
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });
    const [openSidebar, setOpenSidebar] = useState(false);
    const open = isMd ? false : openSidebar;

    const handleDelete = async (rowData) => {
        if (!window.confirm('Are you sure you want to delete this batch? Active students will be unassigned.')) return;
        dispatch(setGlobalLoader(true));
        try {
            await deleteBatch(rowData._id);
            toast.success('Batch deleted successfully');
            if (tableRef.current) tableRef.current.onQueryChange();
        } catch (error) {
            toast.error(error?.response?.data?.message ?? 'Error deleting batch');
        } finally {
            dispatch(setGlobalLoader(false));
        }
    };

    const columns = [
        { title: 'Sr No.', field: 'index', filtering: false },
        { title: 'Name', field: 'name', filtering: false },
        { title: 'Notes', field: 'notes', filtering: false, render: (rowData) => rowData.notes || '—' },
        {
            title: 'Actions',
            field: '',
            filtering: false,
            render: (rowData) => (
                <div className="tableActions">
                    <Tooltip title="View">
                        <VisibilityIcon
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/batches/view/${rowData._id}`)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <EditIcon
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                                navigate(`/batches/edit/${rowData._id}`, { state: { batchData: rowData } })
                            }
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <DeleteIcon
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleDelete(rowData)}
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    const fetchBatches = async (query) => {
        return new Promise(async (resolve) => {
            try {
                dispatch(setGlobalLoader(true));
                const page = query.page + 1;
                const limit = query.pageSize;
                const res = await getBatches(`?page=${page}&limit=${limit}`);
                const apiData = res?.data?.data;
                const docs = (apiData?.docs || []).map((b, i) => ({
                    ...b,
                    index: (page - 1) * limit + i + 1,
                }));
                dispatch(setGlobalLoader(false));
                resolve({ data: docs, page: query.page, totalCount: apiData?.total || 0 });
            } catch (error) {
                dispatch(setGlobalLoader(false));
                toast.error(error?.response?.data?.message ?? 'Error fetching batches');
                resolve({ data: [], page: 0, totalCount: 0 });
            }
        });
    };

    return (
        <Box>
            <AppBar
                position="fixed"
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={() => setOpenSidebar(true)} />
                </Container>
            </AppBar>
            <Sidebar
                onClose={() => setOpenSidebar(false)}
                open={open}
                variant={isMd ? 'permanent' : 'temporary'}
            />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <Box
                    display="flex"
                    flex="1 1 auto"
                    overflow="hidden"
                    paddingLeft={{ md: '256px' }}
                >
                    <Box display="flex" flex="1 1 auto" overflow="hidden">
                        <Box flex="1 1 auto" height="100%" overflow="auto">
                            <Box px={{ xs: 3, md: 4 }} pt={3} display="flex" flexDirection="column" gap={2}>
                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/batches/add')}
                                    sx={{ alignSelf: 'flex-start' }}
                                >
                                    Add Batch
                                </Button>
                                <Alert severity="info">
                                    Only students with status <strong>In Progress</strong> are shown inside a batch.
                                </Alert>
                                <Alert severity="warning" >
                                    By default, a maximum of <strong>10 batches</strong> can be created per organization. If you need to increase this limit, please contact your administrator or reach out to support.
                                </Alert>
                            </Box>
                            <Box p={4}>
                                <Box
                                    width={1}
                                    height={1}
                                    borderRadius={2}
                                    border={`2px solid ${theme.palette.divider}`}
                                    sx={{ borderStyle: 'dashed' }}
                                >
                                    <Table
                                        ref={tableRef}
                                        columns={columns}
                                        data={fetchBatches}
                                        export={false}
                                        selection={false}
                                        toolbar
                                        title="Batches"
                                        isRemoteData={true}
                                    />
                                </Box>
                            </Box>
                            <Divider />
                        </Box>
                    </Box>
                </Box>
            </main>
        </Box>
    );
};

export default Batches;
